import { useCallback, useEffect, useRef, useState } from "react";
import { checkDbtRegisteredByMobile } from "../../services/loginService";

const normalizeMobile = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

/**
 * Mobile-parity pre-OTP DBT hint — POST /individual/v1/dbt/_registered.
 * Debounced on `mobile`; exposes cached lookup for post-OTP branch routing.
 */
export default function useDbtRegistrationHint(mobile) {
  const cacheRef = useRef({ phone: "", isRegistered: null });
  const [state, setState] = useState({
    checkedPhone: "",
    isRegistered: null,
    loading: false,
  });

  const fetchForPhone = useCallback(async (phone) => {
    const digits = normalizeMobile(phone);
    if (digits.length !== 10) return null;

    if (
      cacheRef.current.phone === digits &&
      cacheRef.current.isRegistered !== null
    ) {
      return cacheRef.current.isRegistered;
    }

    setState((prev) => ({
      ...prev,
      checkedPhone: digits,
      loading: true,
    }));

    try {
      const response = await checkDbtRegisteredByMobile(digits);
      if (response?.status && response.status >= 400) {
        cacheRef.current = { phone: digits, isRegistered: null };
        setState({ checkedPhone: digits, isRegistered: null, loading: false });
        return null;
      }

      const isRegistered = response?.data?.isRegistered === true;
      cacheRef.current = { phone: digits, isRegistered };
      setState({ checkedPhone: digits, isRegistered, loading: false });
      return isRegistered;
    } catch {
      cacheRef.current = { phone: digits, isRegistered: null };
      setState({ checkedPhone: digits, isRegistered: null, loading: false });
      return null;
    }
  }, []);

  useEffect(() => {
    const digits = normalizeMobile(mobile);
    if (digits.length !== 10) {
      cacheRef.current = { phone: "", isRegistered: null };
      setState({ checkedPhone: "", isRegistered: null, loading: false });
      return undefined;
    }

    if (
      cacheRef.current.phone === digits &&
      cacheRef.current.isRegistered !== null
    ) {
      setState({
        checkedPhone: digits,
        isRegistered: cacheRef.current.isRegistered,
        loading: false,
      });
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!cancelled) {
        fetchForPhone(digits);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [mobile, fetchForPhone]);

  const normalizedMobile = normalizeMobile(mobile);
  const showHint =
    normalizedMobile.length === 10 &&
    state.checkedPhone === normalizedMobile &&
    state.isRegistered !== null;

  return {
    loading: state.loading && normalizedMobile.length === 10,
    isRegistered: showHint ? state.isRegistered : null,
    fetchForPhone,
  };
}
